/**
 * Configuration du module d'authentification Caritas
 * Ce fichier définit la configuration globale du module réutilisable
 *
 * RÉUTILISABILITÉ : 100% - TypeScript pur
 * Peut être utilisé dans Next.js, React (Vite, CRA), Remix, etc.
 */

import { configureHttpClient } from '@shared/config/httpConfig';
import { logout } from '../infrastructure/services/tokenManager';

export interface AuthModuleConfig {
  /**
   * URL de base de l'API backend
   * @example 'http://localhost:3000' (Next.js)
   * @example 'https://api.caritas.org' (React + Express)
   */
  apiBaseUrl: string;

  /**
   * Endpoints de l'API (configurables)
   */
  endpoints: {
    // Authentification
    login: string;
    logout: string;
    refresh: string;
    verify2FA: string;

    // Gestion profil
    getProfile: string;
    updateProfile: string;
    changePassword: string;
    changeEmail: string;
    verifyEmail: string;
    verifyEmailChange: string;
    deleteAccount: string;

    // 2FA
    activate2FA: string;
    deactivate2FA: string;
    generate2FASecret: string;
    send2FACode: string;
    verifyBackupCode: string;
    generateBackupCodes: string;

    // Sessions
    listSessions: string;
    revokeSession: string;
    revokeAllSessions: string;

    // Tokens
    validateToken: string;
    revokeToken: string;
    revokeAllTokens: string;

    // Mot de passe
    requestPasswordReset: string;
    verifyResetToken: string;
    resetPassword: string;

    // Sécurité
    getSecurityParams: string;
    updateSecurityParams: string;
    getHistory: string;
    getSecurityLogs: string;
  };

  /**
   * Configuration des tokens JWT
   */
  tokens: {
    /**
     * Durée avant expiration du access token (millisecondes)
     * Par défaut: 24h
     */
    accessTokenExpiry: number;

    /**
     * Durée avant expiration du refresh token (millisecondes)
     * Par défaut: 7 jours
     */
    refreshTokenExpiry: number;

    /**
     * Auto-refresh du token avant expiration
     */
    autoRefresh: boolean;

    /**
     * Marge avant expiration pour déclencher le refresh (millisecondes)
     * Par défaut: 5 minutes
     */
    refreshMargin: number;
  };

  /**
   * Configuration de sécurité
   */
  security: {
    /**
     * Activer le stockage sécurisé (httpOnly cookies)
     * Note: Nécessite backend supportant les cookies
     */
    useSecureStorage: boolean;

    /**
     * Activer la détection de connexions suspectes
     */
    detectSuspiciousActivity: boolean;

    /**
     * Rediriger automatiquement si non authentifié
     */
    autoRedirectOnUnauth: boolean;

    /**
     * URL de redirection si non authentifié
     */
    loginUrl: string;

    /**
     * URL de redirection après connexion réussie
     */
    defaultRedirectAfterLogin: string;
  };

  /**
   * Configuration UI
   */
  ui: {
    /**
     * Afficher les messages de toast
     */
    showToasts: boolean;

    /**
     * Durée d'affichage des toasts (millisecondes)
     */
    toastDuration: number;

    /**
     * Langue par défaut
     */
    defaultLocale: 'fr' | 'en';
  };
}

/**
 * Configuration par défaut pour Next.js
 */
export const defaultNextJsConfig: AuthModuleConfig = {
  apiBaseUrl: '',  // URL relative pour Next.js
  endpoints: {
    // Authentification
    login: '/api/auth/connexion',
    logout: '/api/auth/logout',
    refresh: '/api/auth/refresh',
    verify2FA: '/api/auth/verify-2fa',

    // Gestion profil
    getProfile: '/api/auth/profile',
    updateProfile: '/api/auth/profile',
    changePassword: '/api/auth/change-password',
    changeEmail: '/api/auth/change-email',
    verifyEmail: '/api/auth/verify-email',
    verifyEmailChange: '/api/auth/verify-email-change',
    deleteAccount: '/api/auth/delete-account',

    // 2FA
    activate2FA: '/api/auth/activate-2fa',
    deactivate2FA: '/api/auth/2fa/deactivate',
    generate2FASecret: '/api/auth/2fa/generate-secret',
    send2FACode: '/api/auth/2fa/send-code',
    verifyBackupCode: '/api/auth/2fa/backup-codes/verify',
    generateBackupCodes: '/api/auth/2fa/backup-codes/generate',

    // Sessions
    listSessions: '/api/auth/sessions',
    revokeSession: '/api/auth/sessions/revoke',
    revokeAllSessions: '/api/auth/sessions/revoke-all',

    // Tokens
    validateToken: '/api/auth/tokens/validate',
    revokeToken: '/api/auth/tokens/revoke',
    revokeAllTokens: '/api/auth/tokens/revoke-all',

    // Mot de passe
    requestPasswordReset: '/api/auth/reset-password/request',
    verifyResetToken: '/api/auth/reset-password/verify',
    resetPassword: '/api/auth/reset-password/confirm',

    // Sécurité
    getSecurityParams: '/api/auth/parametres',
    updateSecurityParams: '/api/auth/parametres',
    getHistory: '/api/auth/history',
    getSecurityLogs: '/api/auth/security/logs',
  },
  tokens: {
    accessTokenExpiry: 24 * 60 * 60 * 1000,  // 24 heures
    refreshTokenExpiry: 7 * 24 * 60 * 60 * 1000,  // 7 jours
    autoRefresh: true,
    refreshMargin: 5 * 60 * 1000,  // 5 minutes
  },
  security: {
    useSecureStorage: false,  // localStorage par défaut
    detectSuspiciousActivity: true,
    autoRedirectOnUnauth: true,
    loginUrl: '/connexion',
    defaultRedirectAfterLogin: '/dashboard',
  },
  ui: {
    showToasts: true,
    toastDuration: 5000,  // 5 secondes
    defaultLocale: 'fr',
  },
};

/**
 * Configuration par défaut pour React pur (avec backend séparé)
 */
export const defaultReactConfig: AuthModuleConfig = {
  apiBaseUrl: '',  // URL relative pour Vite proxy (proxy /api → backend)
  endpoints: {
    // Authentification (memes chemins que le backend Express)
    login: '/api/auth/connexion',
    logout: '/api/auth/logout',
    refresh: '/api/auth/refresh',
    verify2FA: '/api/auth/2fa/verify',

    // Gestion profil
    getProfile: '/api/auth/profile',
    updateProfile: '/api/auth/profile',
    changePassword: '/api/auth/change-password',
    changeEmail: '/api/auth/change-email',
    verifyEmail: '/api/auth/verify-email',
    verifyEmailChange: '/api/auth/verify-email-change',
    deleteAccount: '/api/auth/delete-account',

    // 2FA
    activate2FA: '/api/auth/2fa/activate',
    deactivate2FA: '/api/auth/2fa/deactivate',
    generate2FASecret: '/api/auth/2fa/generate-secret',
    send2FACode: '/api/auth/2fa/send-code',
    verifyBackupCode: '/api/auth/2fa/backup-codes/verify',
    generateBackupCodes: '/api/auth/2fa/backup-codes/generate',

    // Sessions
    listSessions: '/api/auth/sessions',
    revokeSession: '/api/auth/sessions/revoke',
    revokeAllSessions: '/api/auth/sessions/revoke-all',

    // Tokens
    validateToken: '/api/auth/tokens/validate',
    revokeToken: '/api/auth/tokens/revoke',
    revokeAllTokens: '/api/auth/tokens/revoke-all',

    // Mot de passe
    requestPasswordReset: '/api/auth/reset-password/request',
    verifyResetToken: '/api/auth/reset-password/verify',
    resetPassword: '/api/auth/reset-password/confirm',

    // Securite
    getSecurityParams: '/api/auth/parametres',
    updateSecurityParams: '/api/auth/parametres',
    getHistory: '/api/auth/history',
    getSecurityLogs: '/api/auth/security/logs',
  },
  tokens: {
    accessTokenExpiry: 24 * 60 * 60 * 1000,
    refreshTokenExpiry: 7 * 24 * 60 * 60 * 1000,
    autoRefresh: true,
    refreshMargin: 5 * 60 * 1000,
  },
  security: {
    useSecureStorage: false,
    detectSuspiciousActivity: true,
    autoRedirectOnUnauth: true,
    loginUrl: '/login',
    defaultRedirectAfterLogin: '/dashboard',
  },
  ui: {
    showToasts: true,
    toastDuration: 5000,
    defaultLocale: 'fr',
  },
};

/**
 * Configuration actuelle du module (initialisée avec Next.js par défaut)
 */
let currentConfig: AuthModuleConfig = defaultNextJsConfig;

/**
 * Configure le module d'authentification
 * Cette fonction doit être appelée avant d'utiliser le module
 *
 * @example Next.js
 * ```tsx
 * // app/layout.tsx ou main.tsx
 * import { configureAuthModule } from '@/modules/auth-caritas';
 *
 * configureAuthModule({
 *   // Utilise la config par défaut Next.js
 *   ...defaultNextJsConfig,
 *   security: {
 *     ...defaultNextJsConfig.security,
 *     loginUrl: '/auth/connexion',  // Override
 *   }
 * });
 * ```
 *
 * @example React pur avec Express backend
 * ```tsx
 * // main.tsx
 * import { configureAuthModule, defaultReactConfig } from '@caritas/auth-react';
 *
 * configureAuthModule({
 *   ...defaultReactConfig,
 *   apiBaseUrl: 'https://api.myapp.com',  // Production API
 * });
 * ```
 */
export function configureAuthModule(config: Partial<AuthModuleConfig>) {
  currentConfig = {
    ...currentConfig,
    ...config,
    endpoints: {
      ...currentConfig.endpoints,
      ...config.endpoints,
    },
    tokens: {
      ...currentConfig.tokens,
      ...config.tokens,
    },
    security: {
      ...currentConfig.security,
      ...config.security,
    },
    ui: {
      ...currentConfig.ui,
      ...config.ui,
    },
  };

  // Synchroniser la configuration HTTP partagee (@shared)
  configureHttpClient({
    apiBaseUrl: currentConfig.apiBaseUrl,
    tokens: {
      autoRefresh: currentConfig.tokens.autoRefresh,
      refreshMargin: currentConfig.tokens.refreshMargin,
    },
    auth: {
      refreshEndpoint: currentConfig.endpoints.refresh,
      loginUrl: currentConfig.security.loginUrl,
      onUnauthorized: () => {
        logout();
        if (typeof window !== 'undefined') {
          window.location.href = currentConfig.security.loginUrl;
        }
      },
    },
  });
}

/**
 * Récupère la configuration actuelle du module
 */
export function getAuthConfig(): AuthModuleConfig {
  return currentConfig;
}

/**
 * Réinitialise la configuration aux valeurs par défaut
 * Utile pour les tests
 */
export function resetAuthConfig() {
  currentConfig = defaultNextJsConfig;
}
